import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// Get all vendor products
export const getVendorProducts = createAsyncThunk(
  "vendorProducts/getAll",
  async ({ status, search, sortBy }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (search) params.append("search", search);
      if (sortBy) params.append("sortBy", sortBy);

      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/vendor/products?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch products" }
      );
    }
  }
);

// Get single vendor product
export const getVendorProduct = createAsyncThunk(
  "vendorProducts/getOne",
  async (productId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/vendor/products/${productId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch product" }
      );
    }
  }
);

// Create vendor product
export const createVendorProduct = createAsyncThunk(
  "vendorProducts/create",
  async (productData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/api/vendor/products`,
        productData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            "Content-Type": "application/json", // ✅ JSON now, not FormData
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to create product" }
      );
    }
  }
);

// Update vendor product
export const updateVendorProduct = createAsyncThunk(
  "vendorProducts/update",
  async ({ productId, productData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${import.meta.env.VITE_BACKEND_URL}/api/vendor/products/${productId}`,
        productData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            // Don't set Content-Type for FormData
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to update product" }
      );
    }
  }
);

// Delete vendor product
export const deleteVendorProduct = createAsyncThunk(
  "vendorProducts/delete",
  async (productId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${import.meta.env.VITE_BACKEND_URL}/api/vendor/products/${productId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      return { productId, message: response.data.message };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to delete product" }
      );
    }
  }
);

// Toggle product publish status
export const toggleProductPublish = createAsyncThunk(
  "vendorProducts/togglePublish",
  async (productId, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${import.meta.env.VITE_BACKEND_URL}/api/vendor/products/${productId}/publish`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to toggle publish status" }
      );
    }
  }
);

const vendorProductSlice = createSlice({
  name: "vendorProducts",
  initialState: {
    products: [],
    selectedProduct: null,
    loading: false,
    error: null,
    createLoading: false,
    updateLoading: false,
    deleteLoading: false,
  },
  reducers: {
    clearProductError: (state) => {
      state.error = null;
    },
    clearSelectedProduct: (state) => {
      state.selectedProduct = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get All Products
      .addCase(getVendorProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVendorProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.products;
        state.error = null;
      })
      .addCase(getVendorProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch products";
      })

      // Get Single Product
      .addCase(getVendorProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVendorProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedProduct = action.payload;
        state.error = null;
      })
      .addCase(getVendorProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch product";
      })

      // Create Product
      .addCase(createVendorProduct.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createVendorProduct.fulfilled, (state, action) => {
        state.createLoading = false;
        state.products.unshift(action.payload.product);
        state.error = null;
      })
      .addCase(createVendorProduct.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload?.message || "Failed to create product";
      })

      // Update Product
      .addCase(updateVendorProduct.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateVendorProduct.fulfilled, (state, action) => {
        state.updateLoading = false;
        const index = state.products.findIndex(
          (p) => p._id === action.payload.product._id
        );
        if (index !== -1) {
          state.products[index] = action.payload.product;
        }
        state.selectedProduct = action.payload.product;
        state.error = null;
      })
      .addCase(updateVendorProduct.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload?.message || "Failed to update product";
      })

      // Delete Product
      .addCase(deleteVendorProduct.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteVendorProduct.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.products = state.products.filter(
          (p) => p._id !== action.payload.productId
        );
        state.error = null;
      })
      .addCase(deleteVendorProduct.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload?.message || "Failed to delete product";
      })

      // Toggle Publish
      .addCase(toggleProductPublish.fulfilled, (state, action) => {
        const index = state.products.findIndex(
          (p) => p._id === action.payload.product._id
        );
        if (index !== -1) {
          state.products[index] = action.payload.product;
        }
      });
  },
});

export const { clearProductError, clearSelectedProduct } = vendorProductSlice.actions;
export default vendorProductSlice.reducer;
